import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, Loader2, AlertTriangle } from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { LoadingBlock } from "@/components/Pagination";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";

const ORG_TYPES = [
  ["hospital", "Hospital"],
  ["medical_college", "Medical College"],
  ["nursing_institute", "Nursing Institute"],
  ["diagnostic_center", "Diagnostic Center"],
  ["healthcare_training_institute", "Healthcare Training Institute"],
  ["corporate_healthcare_organization", "Corporate Healthcare Organization"],
  ["individual_learner", "Individual Learner"],
];

const STATUSES = [
  ["pending_setup", "Pending Setup"],
  ["active", "Active"],
  ["inactive", "Inactive"],
  ["suspended", "Suspended"],
];

const EMPTY = {
  organization_name: "", organization_code: "", organization_type: "hospital",
  legal_name: "", registration_number: "", gst_number: "", pan_number: "",
  website: "", email: "", mobile: "", alternate_mobile: "",
  address_line_1: "", address_line_2: "", city: "", district: "",
  state: "", country: "India", pincode: "",
  primary_contact_name: "", primary_contact_designation: "",
  primary_contact_email: "", primary_contact_mobile: "",
  logo: "", primary_color: "#101876", secondary_color: "#329112",
  status: "pending_setup", max_users: "",
  subscription_plan: "", plan_start_date: "", plan_end_date: "", billing_status: "",
  timezone: "Asia/Kolkata", default_language: "en",
};

function F({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">
        {label}{required && <span className="ml-1 text-[#329112]">*</span>}
      </Label>
      {children}
    </div>
  );
}

export default function AdminOrganizationFormPage() {
  const { orgId } = useParams();
  const isEdit = Boolean(orgId);
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [initialCode, setInitialCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [codeWarning, setCodeWarning] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const load = useCallback(async () => {
    if (!isEdit) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/organizations-summary/${orgId}`);
      const o = data.data.organization;
      setForm({ ...EMPTY, ...o, max_users: o.max_users ?? "" });
      setInitialCode(o.organization_code);
    } catch (err) {
      toast.error(formatApiError(err));
      navigate("/app/super-admin/organizations");
    } finally {
      setLoading(false);
    }
  }, [isEdit, orgId, navigate]);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (isEdit && form.organization_code !== initialCode && !codeWarning) {
      setCodeWarning(true);
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.max_users === "") payload.max_users = null;
      else payload.max_users = Number(payload.max_users);
      // remove empty strings so backend keeps defaults
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "") payload[k] = null;
      });
      if (isEdit) {
        await api.put(`/admin/organizations/${orgId}`, payload);
        toast.success("Organization updated.");
      } else {
        const { data } = await api.post("/admin/organizations", payload);
        toast.success("Organization created.");
        navigate(`/app/super-admin/organizations/${data.data.organization.id}`);
        return;
      }
      navigate(`/app/super-admin/organizations/${orgId}`);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout title={isEdit ? "Edit organization" : "Add organization"} breadcrumbs={["Platform", "Organizations", isEdit ? "Edit" : "Add"]}>
      <button onClick={() => navigate(-1)} className="mb-4 inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-[#101876]">
        <ArrowLeft className="h-3.5 w-3.5" /> Back
      </button>
      {loading ? <LoadingBlock label="Loading…" /> : (
      <form onSubmit={submit} className="max-w-4xl space-y-4" data-testid="admin-org-form">
        {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
        {codeWarning && (
          <Alert className="border-amber-200 bg-amber-50 text-amber-800">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Changing an organization code can break existing integrations. Click Save again to confirm.
            </AlertDescription>
          </Alert>
        )}

        <Tabs defaultValue="basic">
          <TabsList className="bg-white border border-slate-200 rounded-lg p-1">
            <TabsTrigger value="basic" className="data-[state=active]:bg-[#101876] data-[state=active]:text-white">Basic</TabsTrigger>
            <TabsTrigger value="address" className="data-[state=active]:bg-[#101876] data-[state=active]:text-white">Address</TabsTrigger>
            <TabsTrigger value="contact" className="data-[state=active]:bg-[#101876] data-[state=active]:text-white">Contact</TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-[#101876] data-[state=active]:text-white">Settings</TabsTrigger>
            <TabsTrigger value="branding" className="data-[state=active]:bg-[#101876] data-[state=active]:text-white">Branding</TabsTrigger>
          </TabsList>

          <TabsContent value="basic">
            <section className="mt-4 rounded-2xl border border-slate-200 bg-white p-6">
              <div className="grid gap-4 sm:grid-cols-2">
                <F label="Organization name" required><Input required value={form.organization_name} onChange={set("organization_name")} data-testid="org-form-name" /></F>
                <F label="Organization code" required><Input required value={form.organization_code} onChange={set("organization_code")} data-testid="org-form-code" /></F>
                <F label="Organization type" required>
                  <Select value={form.organization_type} onValueChange={set("organization_type")}>
                    <SelectTrigger data-testid="org-form-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ORG_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </F>
                <F label="Status" required>
                  <Select value={form.status} onValueChange={set("status")}>
                    <SelectTrigger data-testid="org-form-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUSES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </F>
                <F label="Legal name"><Input value={form.legal_name} onChange={set("legal_name")} /></F>
                <F label="Registration number"><Input value={form.registration_number} onChange={set("registration_number")} /></F>
                <F label="GSTIN" required={false}>
                  <Input value={form.gst_number} onChange={set("gst_number")}
                    placeholder="e.g. 27ABCDE1234F1Z5"
                    className="uppercase font-mono"
                    data-testid="org-form-gstin" />
                </F>
                <F label="PAN number"><Input value={form.pan_number} onChange={set("pan_number")} /></F>
                <F label="Website"><Input value={form.website} onChange={set("website")} placeholder="https://" /></F>
                <F label="Organization email"><Input type="email" value={form.email} onChange={set("email")} data-testid="org-form-email" /></F>
                <F label="Organization mobile"><Input value={form.mobile} onChange={set("mobile")} /></F>
                <F label="Alternate mobile"><Input value={form.alternate_mobile} onChange={set("alternate_mobile")} /></F>
              </div>
            </section>
          </TabsContent>

          <TabsContent value="address">
            <section className="mt-4 rounded-2xl border border-slate-200 bg-white p-6 grid gap-4 sm:grid-cols-2">
              <F label="Address line 1"><Input value={form.address_line_1} onChange={set("address_line_1")} /></F>
              <F label="Address line 2"><Input value={form.address_line_2} onChange={set("address_line_2")} /></F>
              <F label="City"><Input value={form.city} onChange={set("city")} /></F>
              <F label="District"><Input value={form.district} onChange={set("district")} /></F>
              <F label="State"><Input value={form.state} onChange={set("state")} /></F>
              <F label="Country"><Input value={form.country} onChange={set("country")} /></F>
              <F label="Pincode"><Input value={form.pincode} onChange={set("pincode")} /></F>
            </section>
          </TabsContent>

          <TabsContent value="contact">
            <section className="mt-4 rounded-2xl border border-slate-200 bg-white p-6 grid gap-4 sm:grid-cols-2">
              <F label="Contact person name"><Input value={form.primary_contact_name} onChange={set("primary_contact_name")} /></F>
              <F label="Contact designation"><Input value={form.primary_contact_designation} onChange={set("primary_contact_designation")} /></F>
              <F label="Contact email"><Input type="email" value={form.primary_contact_email} onChange={set("primary_contact_email")} /></F>
              <F label="Contact mobile"><Input value={form.primary_contact_mobile} onChange={set("primary_contact_mobile")} /></F>
            </section>
          </TabsContent>

          <TabsContent value="settings">
            <section className="mt-4 rounded-2xl border border-slate-200 bg-white p-6 grid gap-4 sm:grid-cols-2">
              <F label="Maximum users"><Input type="number" value={form.max_users} onChange={set("max_users")} /></F>
              <F label="Subscription plan"><Input value={form.subscription_plan} onChange={set("subscription_plan")} placeholder="e.g. Enterprise 500" /></F>
              <F label="Plan start date"><Input type="date" value={form.plan_start_date?.split("T")[0] || ""} onChange={set("plan_start_date")} /></F>
              <F label="Plan end date"><Input type="date" value={form.plan_end_date?.split("T")[0] || ""} onChange={set("plan_end_date")} /></F>
              <F label="Billing status">
                <Select value={form.billing_status || ""} onValueChange={set("billing_status")}>
                  <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="trial">Trial</SelectItem>
                    <SelectItem value="past_due">Past due</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </F>
              <F label="Timezone"><Input value={form.timezone} onChange={set("timezone")} /></F>
              <F label="Default language"><Input value={form.default_language} onChange={set("default_language")} /></F>
            </section>
          </TabsContent>

          <TabsContent value="branding">
            <section className="mt-4 rounded-2xl border border-slate-200 bg-white p-6 grid gap-4 sm:grid-cols-2">
              <F label="Logo URL"><Input value={form.logo} onChange={set("logo")} placeholder="https://cdn.example.com/logo.png" /></F>
              <F label="Primary color">
                <div className="flex items-center gap-3">
                  <input type="color" value={form.primary_color || "#101876"} onChange={set("primary_color")} className="h-10 w-14 cursor-pointer rounded-md border border-slate-200" />
                  <Input value={form.primary_color} onChange={set("primary_color")} />
                </div>
              </F>
              <F label="Secondary color">
                <div className="flex items-center gap-3">
                  <input type="color" value={form.secondary_color || "#329112"} onChange={set("secondary_color")} className="h-10 w-14 cursor-pointer rounded-md border border-slate-200" />
                  <Input value={form.secondary_color} onChange={set("secondary_color")} />
                </div>
              </F>
            </section>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end gap-2">
          <button type="button" onClick={() => navigate(-1)} className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-md bg-[#101876] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0c135c] disabled:opacity-70"
            data-testid="org-form-submit"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create organization"}
          </button>
        </div>
      </form>
      )}
    </DashboardLayout>
  );
}
